import { useEffect, useRef } from "react";
import { CATEGORY_SCOPE, type MapCategory } from "@/map/config/categories";

export interface MapShortcutsConfig {
  /** Master switch — when false the listener does nothing. */
  enabled?: boolean;
  /** A location is in scope (location-scoped panels are reachable). */
  hasLocation: boolean;
  /** A machine is selected (machine-scoped panels / timeline reachable). */
  hasMachine: boolean;
  /** The on-map alert card is open (enables alert navigation + actions). */
  alertCardOpen: boolean;
  /**
   * The fullscreen image lightbox is open. Only used for Esc routing now: while
   * open Esc closes only the lightbox; while closed Esc runs the close cascade.
   * The arrow-axis mapping (below) is the same whether or not it's open.
   */
  lightboxOpen: boolean;

  // Alert card actions — Up/Down step ALERTS, Left/Right step IMAGES.
  onNextAlert: () => void;
  onPrevAlert: () => void;
  onResolveAlert: () => void;
  onFalseAlert: () => void;
  /**
   * The frames are stacked vertically (Alert Review V2), so Up/Down walk them
   * and Left/Right step alerts. Default: the older card's horizontal carousel.
   */
  verticalFrameStrip?: boolean;

  // Image navigation — drives the shared image index (card + lightbox stay in
  // sync), whether or not the lightbox is open.
  onPrevImage: () => void;
  onNextImage: () => void;
  onCloseLightbox: () => void;

  // Panels
  onToggleCategory: (category: MapCategory) => void;
  onToggleTimeline: () => void;

  /** Cascading close — owner decides what the topmost layer is. */
  onEscape: () => void;
  /** Toggle the shortcuts help overlay. */
  onToggleHelp: () => void;
}

/** Letter → category for the right-rail toggles. */
const CATEGORY_KEYS: Record<string, MapCategory> = {
  k: "kpis",
  a: "alerts",
  e: "events",
  g: "gps",
  v: "video",
  i: "images",
};

const isTypingTarget = (el: EventTarget | null): boolean =>
  el instanceof HTMLInputElement ||
  el instanceof HTMLTextAreaElement ||
  el instanceof HTMLSelectElement ||
  (el instanceof HTMLElement && el.isContentEditable);

/**
 * Keyboard shortcuts for the `/map` page. Registered in the capture phase so the
 * alert-navigation arrows can pre-empt the video player's own arrow shortcuts
 * (which also listen on `window`) while the card is open — otherwise keys fall
 * through to the video/timeline as before.
 *
 * Shortcuts:
 *  - `?`            toggle this help
 *  - `Esc`          close the lightbox if open, else the topmost layer
 *                   (card → panel → timeline → video → machine → org)
 *  - `←` / `→`      previous / next IMAGE (while the alert card is open)
 *  - `↑` / `↓`      previous / next ALERT (while the alert card is open)
 *  - `R` / `F`      resolve / mark-false the open alert
 *  - `K A E`        toggle KPIs / Alerts / Events (location)
 *  - `G V I`        toggle GPS / Video / Images (machine)
 *  - `T`            toggle the timeline (machine)
 */
export const useMapShortcuts = (config: MapShortcutsConfig) => {
  const ref = useRef(config);
  ref.current = config;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const c = ref.current;
      if (c.enabled === false) return;
      if (isTypingTarget(e.target)) return;

      // Help — works with Shift (it's Shift+/) and from anywhere.
      if (e.key === "?") {
        e.preventDefault();
        c.onToggleHelp();
        return;
      }

      // Escape. When the lightbox is open it swallows Esc — close only the
      // lightbox (stop it here so Radix's own Esc-to-close and the card-closing
      // cascade don't also fire). Otherwise run the cascade and let it bubble
      // (the timeline editor cancels its create-flow on Escape).
      if (e.key === "Escape") {
        if (c.lightboxOpen) {
          e.preventDefault();
          e.stopImmediatePropagation();
          c.onCloseLightbox();
          return;
        }
        c.onEscape();
        return;
      }

      // Everything below is an unmodified single-key shortcut.
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

      // Alert open → a fixed axis mapping, and which axis is which follows the
      // FRAMES' own direction. The V2 review stacks the event's frames in a
      // vertical strip, so Up/Down walk the frames and Left/Right step alerts;
      // the older card scrolls its frames horizontally, so it is the other way
      // round. Matching the keys to what the operator sees moving is the whole
      // point — an axis that fights the layout is worse than no shortcut.
      // stopImmediatePropagation beats the video player's own arrow shortcuts.
      if (c.alertCardOpen) {
        const vertical = !!c.verticalFrameStrip;
        const prevFrame = () => c.onPrevImage();
        const nextFrame = () => c.onNextImage();
        const prevAlert = () => c.onPrevAlert();
        const nextAlert = () => c.onNextAlert();
        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            e.stopImmediatePropagation();
            (vertical ? prevAlert : prevFrame)();
            return;
          case "ArrowRight":
            e.preventDefault();
            e.stopImmediatePropagation();
            (vertical ? nextAlert : nextFrame)();
            return;
          case "ArrowUp":
            e.preventDefault();
            e.stopImmediatePropagation();
            (vertical ? prevFrame : prevAlert)();
            return;
          case "ArrowDown":
            e.preventDefault();
            e.stopImmediatePropagation();
            (vertical ? nextFrame : nextAlert)();
            return;
          // `t`/`f` are the verdict on the DETECTION — true positive or false
          // positive — which is what Alert Review V2 asks for. `r` stays as the
          // old Resolve binding so existing muscle memory keeps working.
          case "t":
          case "r":
            e.preventDefault();
            c.onResolveAlert();
            return;
          case "f":
            e.preventDefault();
            c.onFalseAlert();
            return;
        }
      }

      const key = e.key.toLowerCase();

      if (key === "t") {
        if (c.hasMachine) {
          e.preventDefault();
          c.onToggleTimeline();
        }
        return;
      }

      const category = CATEGORY_KEYS[key];
      if (category) {
        const scope = CATEGORY_SCOPE[category];
        const allowed =
          scope === "machine" ? c.hasMachine : scope === "location" ? c.hasLocation : true;
        if (allowed) {
          e.preventDefault();
          c.onToggleCategory(category);
        }
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);
};
